import { Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Deep specifier, not the `@attendance/ui` barrel. This module is on the boot
// path, and the package declares no `sideEffects: false`, so a barrel import
// here would pin all ~35 primitives into the entry chunk — see the note in
// `components/common/Skeleton.tsx`, which is where that had actually happened.
import { Loading } from '@attendance/ui/components/Loading';

/* ===========================================================================
 * THE BOUNDARY EVERY LAZY ROUTE LANDS IN.
 *
 * Split out of `App.tsx` for the same reason `lazyPages.ts` was: that file is
 * a route table, and everything in it that is not a `<Route>` is in front of
 * the thing a reader opened it for. The table stays there — the tests that
 * read `App.tsx` as SOURCE (`routeGates`, `navigationWiring`, `campsPillar`)
 * parse `<Route>` elements, and none of them parse this.
 * ======================================================================== */

/**
 * What a route's chunk arriving looks like.
 *
 * `Loading` from `@attendance/ui`, not a skeleton and not a hand-rolled
 * spinner — §6 of docs/design-rules.md: one loading idiom for the app. A
 * skeleton is a promise about the SHAPE of what is coming, and a route
 * boundary cannot make that promise: the next screen may be a table, a map or
 * a form. The dots say "waiting" without claiming to know for what.
 */
export function RouteFallback(): JSX.Element {
  // The app's words, not the primitive's English default (L-18).
  const { t } = useTranslation();
  return <Loading label={t('common.loading', 'Loading…')} />;
}

/**
 * THE BOUNDARY THAT KEEPS THE CHROME ON SCREEN.
 *
 * A pathless layout route element, rendered INSIDE `AppShell`'s `<Outlet>`.
 * The outer boundary in `App.tsx` (above `<Routes>`) would also catch a page
 * chunk — and in catching it would unmount the rail, the header and the tab
 * bar and replace the whole window with the fallback, so every single
 * navigation flashed the app away and built it again. Scoped here, only the
 * content area waits; the sidebar, the search and the notification bell never
 * move. The outer one stays for the routes ABOVE this layout (the signed-out
 * screens), which have no shell to preserve.
 *
 * A layout route rather than a wrapper around each page: 57 pages would mean
 * 57 copies of the same two lines, and one of them would be forgotten.
 */
export function ShellSuspense(): JSX.Element {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Outlet />
    </Suspense>
  );
}
